// src/features/auth/components/LoginForm.tsx
import React, { useState } from 'react';

const MACD_URL = import.meta.env.VITE_MACD_URL || 'http://localhost:8110';
// Bypass is only honoured in non-production builds — VITE_GOOGLE_SSO_BYPASS has no effect in prod
const bypassEnabled = import.meta.env.VITE_GOOGLE_SSO_BYPASS === 'true' && import.meta.env.MODE !== 'production';
const GOOGLE_SSO_URL = bypassEnabled
  ? `${MACD_URL}/api/auth/dev-sso-bypass/`
  : `${MACD_URL}/accounts/google/login/?next=/api/auth/tesseract-handoff/`;
import { EyeIcon, EyeSlashIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';
import authService from '../services/authService';
import { LoginRequest, RateLimitError } from '../types';
import TwoFactorVerification from './TwoFactorVerification';
import ForgotPasswordPrompt from './ForgotPasswordPrompt';
import PasswordResetModal from './PasswordResetModal';

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onLoginSuccess?: (data: any) => void;
  onForgotPassword?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToRegister, onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitError | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [hasCredentialError, setHasCredentialError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRateLimitInfo(null);
    setHasCredentialError(false);
    setLoading(true);

    if (!email || !password) {
      setError('Email and password are required.');
      setLoading(false);
      return;
    }

    const loginData: LoginRequest = { email, password };

    try {
      const response = await authService.login(loginData);
      
      // Check if 2FA is required
      if (response.requires_2fa && response.temp_token) {
        setRequires2FA(true);
        setTempToken(response.temp_token);
        setError(null);
      } else {
        // Regular login success
        if (onLoginSuccess) {
          onLoginSuccess(response);
        }
      }
    } catch (err: any) {
      // Extract specific error message from API response
      let errorMessage = 'Invalid email or password. Please try again.';
      
      if (err && typeof err === 'object') {
        if (err.message) {
          // ServiceError from apiClient interceptor
          errorMessage = err.message;
        } else if (err.error) {
          errorMessage = err.error;
        } else if (err.detail) {
          errorMessage = err.detail;
        } else if (err.non_field_errors && Array.isArray(err.non_field_errors)) {
          errorMessage = err.non_field_errors[0];
        }
      }
      
      setError(errorMessage);
      setHasCredentialError(true);
      setFailedAttempts(prev => prev + 1);
      
      // Handle rate limiting
      const rateLimitError = authService.handleRateLimit(err);
      if (rateLimitError) {
        setRateLimitInfo(rateLimitError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASuccess = (data: any) => {
    setRequires2FA(false);
    setTempToken(null);
    if (onLoginSuccess) {
      onLoginSuccess(data);
    }
  };

  const handle2FACancel = () => {
    setRequires2FA(false);
    setTempToken(null);
    setError(null);
  };

  // Show 2FA verification if required
  if (requires2FA && tempToken) {
    return (
      <TwoFactorVerification
        tempToken={tempToken}
        email={email}
        onVerificationSuccess={handle2FASuccess}
        onCancel={handle2FACancel}
      />
    );
  }

  return (
    <div className="p-6 sm:p-8 bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded-lg shadow-xl w-full max-w-md">
      <h2 className="text-2xl font-semibold text-center text-theme-text dark:text-dark-theme-text mb-6">
Login to Your Account
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label 
            htmlFor="email"
            className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-1"
          >
            Email Address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setHasCredentialError(false)}
            className="appearance-none block w-full px-3 py-2 border border-theme-border dark:border-dark-theme-border rounded-md shadow-sm placeholder-theme-text-tertiary dark:placeholder-dark-theme-text-tertiary focus:outline-none focus:ring-primary-light dark:focus:ring-primary-dark focus:border-primary-light dark:focus:border-primary-dark sm:text-sm bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text"
          />
        </div>

        <div>
          <label 
            htmlFor="password"
            className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-1"
          >
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setHasCredentialError(false)}
              className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-theme-text-tertiary dark:placeholder-dark-theme-text-tertiary focus:outline-none sm:text-sm bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text pr-10 ${
                hasCredentialError 
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                  : 'border-theme-border dark:border-dark-theme-border focus:ring-primary-light dark:focus:ring-primary-dark focus:border-primary-light dark:focus:border-primary-dark'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text focus:outline-none"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>
          
          {hasCredentialError && !rateLimitInfo && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              Wrong username or password
            </p>
          )}
          
          <p className="mt-2 text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
            Having trouble logging in?{' '}
            <button
              type="button"
              onClick={() => setShowPasswordReset(true)}
              className="font-medium text-primary-light dark:text-primary-dark hover:underline focus:outline-none"
            >
              Reset your password
            </button>
          </p>
        </div>

        {/* Rate Limiting Warning - Only show if we actually have rate limit info */}
        {rateLimitInfo && (
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
            <div className="flex">
              <ClockIcon className="h-5 w-5 text-amber-500" />
              <div className="ml-2">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Too many login attempts. Please wait before trying again.
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  You can try again in {rateLimitInfo.retry_after} seconds.
                </p>
              </div>
            </div>
          </div>
        )}


        <div>
          <button
            type="submit"
            disabled={loading || (rateLimitInfo ? rateLimitInfo.remaining < 1 : false)}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-button hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-primary-light dark:focus:border-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150"
            aria-label="Login"
            data-testid="login-button"
          >
            {loading ? 'Signing you in...' : 'Login'}
          </button>
        </div>

      </form>

      <div className="mt-4">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-theme-border dark:border-dark-theme-border" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary text-theme-text-secondary dark:text-dark-theme-text-secondary">
              Or
            </span>
          </div>
        </div>
        <a
          href={GOOGLE_SSO_URL}
          className="mt-3 w-full flex items-center justify-center gap-3 py-2 px-4 border border-theme-border dark:border-dark-theme-border rounded-md shadow-sm text-sm font-medium text-theme-text dark:text-dark-theme-text bg-theme-bg dark:bg-dark-theme-bg hover:bg-theme-bg-secondary dark:hover:bg-dark-theme-bg-secondary transition-colors"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
          Continue with Google
        </a>
      </div>

      <div className="mt-6 space-y-3 text-center">
        <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
          Don't have an account?{' '}
          <button 
            onClick={onSwitchToRegister} 
            className="font-medium text-primary-light dark:text-primary-dark hover:underline focus:outline-none"
          >
            Create one
          </button>
        </p>
      </div>

      <PasswordResetModal
        isOpen={showPasswordReset}
        onClose={() => setShowPasswordReset(false)}
      />
    </div>
  );
};

export default LoginForm;
